import MDEditor from '@uiw/react-md-editor';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormLabel, TextInput } from '@carbon/react';
import { Add } from '@carbon/react/icons';
import { SortableList, SortableRow, moveItem } from 'src/components/Storefront/SortableList';
import { StorefrontFaqDto } from 'src/services/apis/types';

/** The API's caps (spec §3): 24 questions, 160 characters of question, and 4000 characters of
 * `answerMarkdown` (Plan 05's call, and the one cap this editor does not mirror — `MDEditor` has
 * no `maxLength`, so the server's 422 is the honest enforcement for the answer text). */
export const FAQ_MAX = 24;
export const FAQ_QUESTION_MAX = 160;

export type FaqRow = { question: string; answerMarkdown: string };

/**
 * Coerces whatever the jsonb column holds into rows the editor can render. The generated entity
 * types `faqs` loosely (Swagger cannot describe a jsonb array's items), so this is the one place
 * that decides what a stored pair looks like — and it never throws on a hand-edited row.
 */
export function toFaqRows(input: unknown): FaqRow[] {
  const stored = Array.isArray(input) ? input : [];
  return stored
    .filter((raw): raw is { question?: unknown; answerMarkdown?: unknown } => typeof raw === 'object' && raw !== null)
    .map((raw) => ({
      question: typeof raw.question === 'string' ? raw.question : '',
      answerMarkdown: typeof raw.answerMarkdown === 'string' ? raw.answerMarkdown : '',
    }));
}

/** A pair needs both halves — the API requires two non-empty strings — so an unfinished row is
 * dropped rather than sent as a question with no answer. */
export function toFaqPayload(rows: FaqRow[]): StorefrontFaqDto[] {
  return rows
    .filter((row) => row.question.trim() !== '' && row.answerMarkdown.trim() !== '')
    .slice(0, FAQ_MAX)
    .map((row) => ({ question: row.question.trim(), answerMarkdown: row.answerMarkdown.trim() }));
}

interface FaqsEditorProps {
  items: FaqRow[];
  onChange: (next: FaqRow[]) => void;
}

const FaqsEditor = React.memo(({ items, onChange }: FaqsEditorProps) => {
  const { t } = useTranslation();

  const update = useCallback(
    (index: number, patch: Partial<FaqRow>) => {
      onChange(items.map((row, position) => (position === index ? { ...row, ...patch } : row)));
    },
    [items, onChange]
  );

  const remove = useCallback(
    (index: number) => {
      onChange(items.filter((_row, position) => position !== index));
    },
    [items, onChange]
  );

  const add = useCallback(() => {
    onChange([...items, { question: '', answerMarkdown: '' }]);
  }, [items, onChange]);

  const reorder = useCallback(
    (from: number, to: number) => {
      onChange(moveItem(items, from, to));
    },
    [items, onChange]
  );

  const atCap = items.length >= FAQ_MAX;

  return (
    <fieldset className={'storefront-extras__group'}>
      <legend className={'cds--visually-hidden'}>{t('storefront.extras.faqs')}</legend>
      <p className={'storefront-extras__hint'}>{t('storefront.extras.faqsHint')}</p>

      {items.length === 0 && <p className={'storefront-extras__empty'}>{t('storefront.extras.faqsEmpty')}</p>}

      {items.length > 0 && (
        <SortableList ids={items.map((_row, index) => String(index))} onReorder={reorder}>
          {items.map((row, index) => (
            <SortableRow
              key={index}
              id={String(index)}
              removeLabel={t('storefront.extras.removeFaq')}
              onRemove={() => remove(index)}
            >
              <TextInput
                id={`faq-question-${index}`}
                labelText={t('storefront.extras.question')}
                maxLength={FAQ_QUESTION_MAX}
                value={row.question}
                onChange={(event) => update(index, { question: event.target.value })}
              />
              {/* The editor renders its own toolbar, and a dark wrapper breaks the Carbon theme
               * around it — the same wrapper Content.tsx and MenuItemForm use. */}
              <div data-color-mode={'light'}>
                <FormLabel>{t('storefront.extras.answer')}</FormLabel>
                <MDEditor
                  preview={'edit'}
                  height={160}
                  value={row.answerMarkdown}
                  onChange={(value) => update(index, { answerMarkdown: value ?? '' })}
                />
                <p className={'cds--form__helper-text'}>{t('storefront.extras.answerHint')}</p>
              </div>
            </SortableRow>
          ))}
        </SortableList>
      )}

      <div className={'storefront-extras__add'}>
        <Button renderIcon={Add} kind={'tertiary'} size={'sm'} disabled={atCap} onClick={add}>
          {t('storefront.extras.addFaq')}
        </Button>
        {atCap && <p className={'storefront-extras__empty'}>{t('storefront.extras.faqsFull')}</p>}
      </div>
    </fieldset>
  );
});

FaqsEditor.displayName = 'FaqsEditor';

export { FaqsEditor };
export default FaqsEditor;
